// jobClient — submit-and-poll helper for the async simulation job queue
// (V1.0). POST /simulation/jobs/{run|eqe|cv} enqueues the SAME payload as
// the synchronous endpoint and returns {job_id}; GET /simulation/jobs/{id}
// is polled until a terminal state and `result` carries the identical
// response body the sync endpoint would have returned — so callers can
// swap `axios.post(sync)` for `runJob(kind, payload)` with no other change.
//
// Long solves (coupled + robin JV can take minutes) exceed HTTP/proxy
// timeouts on the sync endpoints; the job flow sidesteps that and feeds
// REAL progress (per solve point for EQE/CV, per stage for JV) to the UI.

import axios from 'axios';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Submit a simulation job and poll it to completion.
 *
 * @param {'run'|'eqe'|'cv'} kind   job endpoint suffix
 * @param {object} payload          same body as the synchronous endpoint
 * @param {object} [opts]
 * @param {function} [opts.onProgress]  called with {current, total, stage}
 *                                      on every successful poll
 * @param {number} [opts.intervalMs]    poll period (default 1800 ms)
 * @param {number} [opts.maxPollFailures] consecutive poll failures tolerated
 *                                      before giving up (network blips —
 *                                      the job keeps running server-side)
 * @returns {Promise<object>} the sync-endpoint-shaped result body
 */
export async function runJob(kind, payload, {
  onProgress = null,
  intervalMs = 1800,
  maxPollFailures = 5,
} = {}) {
  const { data: submitted } = await axios.post(
    `${API}/simulation/jobs/${kind}`, payload);
  const jobId = submitted.job_id;

  let failures = 0;
  for (;;) {
    await sleep(intervalMs);
    let status;
    try {
      ({ data: status } = await axios.get(`${API}/simulation/jobs/${jobId}`));
      failures = 0;
    } catch (e) {
      // Transient polling error (network blip / server hiccup): the job
      // is still running server-side — retry, don't die.
      failures += 1;
      if (failures >= maxPollFailures) throw e;
      continue;
    }
    if (status.progress && onProgress) {
      try { onProgress(status.progress); } catch { /* UI-only — ignore */ }
    }
    if (status.status === 'done') return status.result;
    if (status.status === 'error') {
      throw new Error(status.error || 'Simulation job failed');
    }
    if (status.status === 'cancelled') {
      throw new Error('Simulation job was cancelled');
    }
    // queued | running → keep polling
  }
}

/** Best-effort cancel (hard if still queued, cooperative if running). */
export async function cancelJob(jobId) {
  const { data } = await axios.delete(`${API}/simulation/jobs/${jobId}`);
  return data;
}
